import { IActionCategory, IComponentType, IComponentUnion, IRoot, ISceneComp, IActionDict, ISpatialComponentUnion, IContent360Scene, IComponentsById, ITrackingTypes, IShowArContentScreenRelativeAction, IShowTargetLostPromptAction, INoActionData, ISceneActionData } from "../components/r3f-components/component-data-structure";
import { ITransCatTypes } from "../components/r3f-components/transitions";
import { useMemo } from "react";
import { filterArrayByAnother, isAbstractComponent } from "../components/r3f-components/utils/general";
import { safariMobileInfo } from "./browser";
import { IVideoBackground360 } from "@/components/r3f-components/component-data-structure/types/background360";

interface IComponentDict {
    [id: string]: IComponentUnion;
}

export const transitionAdjustedRenderOrder = (sceneTransCatRef: React.RefObject<ITransCatTypes>, renderOrder: number): number => {
    return renderOrder * (sceneTransCatRef.current === ITransCatTypes.enter ? 1000 : 1)
}

export const contentHasSound = (componentDict: IComponentDict | null, rootId: string | null): boolean => {
	if (!componentDict || !rootId) return false;

    const root = componentDict[rootId] as IRoot;

	const hasBackgroundSound = !!root.bkgrSound;
	if (hasBackgroundSound) return true;

    const hasSceneWithOnloadSound = root.children.filter(sceneId => {
        const scene = componentDict[sceneId] as ISceneComp;
        const onLoadActionData = scene.actions?.onLoad ?? [];
        return onLoadActionData.filter(data => data.type === IActionCategory.playSound).length > 0;
    }).length > 0;

    if (hasSceneWithOnloadSound) return true;

	const componentsWithSound = [] as IComponentUnion[];

	const setHasSoundAction = (component: ISpatialComponentUnion) => {
		if (component.type === IComponentType.Video && !component?.muted) {
			componentsWithSound.push(component);
			return;
		}
		const actions = component.actions || ({} as IActionDict);
		for (const key in component.actions) {
			const actionArray = actions[key as keyof typeof actions] || [];
			for (let i = 0; i < actionArray.length; i++) {
				const action = actionArray[i];
				if (action?.type === IActionCategory.playSound || action?.type === IActionCategory.playVideo) {
					componentsWithSound.push(component);
				}
			}
		}
	};

	for (const componentId in componentDict) {
		const component = componentDict[componentId];
		if (isAbstractComponent(component)) {
            if (component.type === IComponentType.Scene) {
                const scene360 = component as IContent360Scene;
                if (scene360.content360?.type === "video" && !(scene360.content360 as IVideoBackground360)?.muted) {
                    componentsWithSound.push(component);
                }
            }
            continue;
        };
		setHasSoundAction(component);
	}

	return componentsWithSound.length !== 0;
};

export function useCheckBrowserShareAndDowloadAbility()  {
    return useMemo(() => {
        let longPressForImageShare = false;
        let iOS12HideSave = false; 
        let iOSFilesAppForPhotoShare = false;
        let imageOnlyInShareData = false
        const safari = safariMobileInfo()
    
        if (typeof safari !== 'undefined') {
            longPressForImageShare = true;
            imageOnlyInShareData = false;
            const majorVersion = safari.os.majorVersion;
            const minorVersion = safari.os.minorVersion;
            if (majorVersion > 0 && minorVersion > 0) {
                if (majorVersion < 11 || (majorVersion === 11 && minorVersion < 3)) {
                    // console.log('blacklisted')
                }
                if (majorVersion === 12) {
                    // IOS 12 save issue
                    iOS12HideSave = true;
                }
                if (majorVersion >= 13) {
                    longPressForImageShare = false;
                    iOSFilesAppForPhotoShare = true;
                }
                if (majorVersion === 14) {
                    // No edge cases at the moment.
                }
                if (majorVersion >= 15) {
                    iOSFilesAppForPhotoShare = false;
                }
            }
        }
        return ({
            longPressForImageShare,
            iOS12HideSave,
            iOSFilesAppForPhotoShare,
            imageOnlyInShareData
        });
    }, [])
    
}


export const getPausableVideoIdsFromIdArray = (targetIds: string[], componentsById: {[id: string]: IComponentUnion}) => {
    const nonPausableVideoIds = [];
    for (const id in componentsById) {
        const component = componentsById[id];
        if (component.type === IComponentType.Video && component.loop && component.autoplay) {
            nonPausableVideoIds.push(id)
        }
    }
   return filterArrayByAnother(targetIds, nonPausableVideoIds)
}

export const getScreenContentIdFromSceneId = (activeSceneId: string | null, componentsById: IComponentsById | null): string | null => {
	if (!componentsById || !activeSceneId) return null;
    const activeSceneChildren = (componentsById[activeSceneId] as ISceneComp)?.children || [];
	return activeSceneChildren.filter(id => componentsById[id].type === IComponentType.ScreenContent)[0]
}

export const dataURItoFileBlob = async (dataURI: string, fileName: string): Promise<File> => {
	const blob = await fetch(dataURI).then((res) => res.blob());
	const file = new File([blob], fileName, { type: 'image/jpeg' });
	return file;
};

const isTargetLostBehaviourAction = (action: ISceneActionData) => {
    switch (action.type) {
        case IActionCategory.showArContentScreenRelative:
        case IActionCategory.showTargetLostPrompt:
        case IActionCategory.noAction:
            return true;
        default:
            return false;
    }
}

type ITargetLostBehaviourAction = IShowArContentScreenRelativeAction | IShowTargetLostPromptAction | INoActionData;

export const getTargetLostBehaviour = (componentsById: IComponentsById | null, rootId: string | null): ITargetLostBehaviourAction | null => {
    if (!componentsById || !rootId) return null;
    const root = componentsById[rootId] as IRoot;
    let targetLostAction: ITargetLostBehaviourAction | null = null;
    
    for (const sceneId of root.children) {
        const scene = componentsById[sceneId] as ISceneComp;
        if (scene.trackingType === ITrackingTypes.image) {
            const targetLostBehaviourActions = scene.actions?.onTargetImageLost
                ?.filter(action => isTargetLostBehaviourAction(action)) as ITargetLostBehaviourAction[]
            targetLostAction = targetLostBehaviourActions?.[0] ?? { type: IActionCategory.noAction };
            break;
        }
    }
    
    return targetLostAction;
}

